/**
 * useSimulation Hook — Annual Simulation
 *
 * Manages annual simulation runs for a project:
 * start, poll, list, delete.
 *
 * @param {string|null} projectId - Unified project ID
 * @returns {Object} Hook interface
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@vortx/shared-ui';
import {
    startAnnualSimulation,
    getAnnualSimulation,
    listAnnualSimulations,
    deleteAnnualSimulation,
    cancelAnnualSimulation,
    pollAnnualSimulation,
} from '../services/simulationService';

export function useSimulation(projectId) {
    const { currentUser } = useAuth();

    const [runs, setRuns] = useState([]);
    const [activeRun, setActiveRun] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const pollingRef = useRef(null);

    /**
     * Load all simulation runs for the current project.
     */
    const loadRuns = useCallback(async () => {
        if (!currentUser || !projectId) {
            setRuns([]);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const data = await listAnnualSimulations(projectId, currentUser);
            setRuns(data.runs || []);
        } catch (err) {
            console.error('Failed to load simulation runs:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser, projectId]);

    /**
     * Load a single run and set it as active.
     */
    const loadRun = useCallback(async (runId) => {
        if (!currentUser || !projectId) return null;

        try {
            setError(null);
            const run = await getAnnualSimulation(projectId, runId, currentUser);
            setActiveRun(run);
            return run;
        } catch (err) {
            console.error('Failed to load simulation run:', err);
            setError(err.message);
            return null;
        }
    }, [currentUser, projectId]);

    /**
     * Start a new annual simulation.
     * Begins polling automatically.
     *
     * @param {Object} [overrides] - {site_capacity_kw, forecast_mae, forecast_seed, name, ...}
     * @returns {Promise<Object>} Final simulation state
     */
    const startRun = useCallback(async (overrides) => {
        if (!currentUser || !projectId) {
            throw new Error('Authentication and project required');
        }

        try {
            setError(null);
            const result = await startAnnualSimulation(projectId, overrides, currentUser);
            const runId = result.run_id;

            // Set initial active run state
            const initialRun = {
                id: runId,
                status: 'running',
                progress: { phase: 'initializing', message: 'Starting...' },
                config: overrides || {},
                agents: result.agents || [],
            };
            setActiveRun(initialRun);

            // Cancel any existing polling
            if (pollingRef.current) {
                pollingRef.current.cancel();
            }

            // Start polling
            const pollPromise = pollAnnualSimulation(
                projectId,
                runId,
                currentUser,
                (run) => {
                    setActiveRun(run);
                },
                3000,
            );
            pollingRef.current = pollPromise;

            const finalState = await pollPromise;
            // Only update activeRun if polling wasn't cancelled
            // (cancelRun sets pollingRef.current = null before this resolves)
            if (pollingRef.current !== null) {
                setActiveRun(finalState);
            }
            pollingRef.current = null;

            // Refresh runs list
            await loadRuns();

            return finalState;
        } catch (err) {
            console.error('Simulation failed:', err);
            setError(err.message);
            throw err;
        }
    }, [currentUser, projectId, loadRuns]);

    /**
     * Delete a simulation run.
     */
    const deleteRun = useCallback(async (runId) => {
        if (!currentUser || !projectId) return;

        try {
            setError(null);
            await deleteAnnualSimulation(projectId, runId, currentUser);
            setRuns(prev => prev.filter(r => r.id !== runId));
            if (activeRun && activeRun.id === runId) {
                setActiveRun(null);
            }
        } catch (err) {
            console.error('Failed to delete run:', err);
            setError(err.message);
        }
    }, [currentUser, projectId, activeRun]);

    /**
     * Cancel a running simulation.
     */
    const cancelRun = useCallback(async (runId) => {
        if (!currentUser || !projectId) return;

        try {
            setError(null);
            await cancelAnnualSimulation(projectId, runId, currentUser);
            // Stop polling
            if (pollingRef.current && pollingRef.current.cancel) {
                pollingRef.current.cancel();
                pollingRef.current = null;
            }
            // Go straight to past runs table (skip "Simulation Cancelled" card)
            setActiveRun(null);
            // Refresh runs list so cancelled run appears in history
            await loadRuns();
        } catch (err) {
            console.error('Failed to cancel simulation:', err);
            setError(err.message);
        }
    }, [currentUser, projectId, loadRuns]);

    // Auto-load runs when projectId changes
    useEffect(() => {
        if (projectId) {
            loadRuns();
        } else {
            setRuns([]);
            setActiveRun(null);
        }
    }, [projectId, loadRuns]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingRef.current && pollingRef.current.cancel) {
                pollingRef.current.cancel();
            }
        };
    }, []);

    return {
        runs,
        activeRun,
        loading,
        error,
        loadRuns,
        loadRun,
        startRun,
        cancelRun,
        deleteRun,
        setActiveRun,
    };
}
